/**
 * REST / Query Manager translation (chartmind-translate).
 * Shared by ChartMind and any feature that needs string-in/string-out translation.
 */

import { llmProviderService } from '../llm/llmProviderService';

const parseTranslationChunk = (chunk) => {
  const c = (chunk || '').trim();
  if (!c) return '';

  if (c.startsWith('{')) {
    try {
      const o = JSON.parse(c);
      if (o && typeof o.translation === 'string') return o.translation.trim();
    } catch (e) {
      console.warn('[restTranslation] Failed to parse single translation JSON object', e);
    }
  }

  const parts = [];
  let i = 0;
  while (i < c.length) {
    const start = c.indexOf('{', i);
    if (start < 0) break;
    let depth = 0;
    let j = start;
    for (; j < c.length; j += 1) {
      const ch = c[j];
      if (ch === '{') depth += 1;
      else if (ch === '}') {
        depth -= 1;
        if (depth === 0) {
          const slice = c.slice(start, j + 1);
          try {
            const o = JSON.parse(slice);
            if (o && typeof o.translation === 'string') parts.push(o.translation.trim());
          } catch (e) {
            if (slice.includes('"translation"')) {
              console.warn('[restTranslation] Malformed translation JSON in chunk', e);
            }
          }
          i = j + 1;
          break;
        }
      }
    }
    if (j >= c.length) break;
  }

  if (parts.length > 0) return parts.join(' ');
  return c;
};

/**
 * @param {unknown} raw - LLM output string or { translation } or { output: string }
 * @returns {string}
 */
export const normalizeTranslationLLMOutput = (raw) => {
  if (raw == null) return '';
  if (typeof raw === 'object' && raw !== null && typeof raw.translation === 'string') {
    return raw.translation.trim();
  }

  const s =
    typeof raw === 'string'
      ? raw.trim()
      : String(raw?.output ?? raw?.content ?? raw?.text ?? '').trim();

  if (!s) return '';

  const fencedBodies = [];
  const fenceRe = /```(?:json)?\s*([\s\S]*?)```/gi;
  let m;
  while ((m = fenceRe.exec(s)) !== null) {
    fencedBodies.push(m[1].trim());
  }

  const chunks = fencedBodies.length > 0 ? fencedBodies : [s];
  const texts = chunks.map((chunk) => parseTranslationChunk(chunk)).filter(Boolean);
  const joined = texts.join(' ').trim();
  if (joined) return joined;

  return parseTranslationChunk(s);
};

/**
 * @param {string} userInput - Full user message for chartmind-translate
 * @param {Object} [options]
 * @param {string} [options.module='chartmind']
 * @param {string} [options.featureName='translation']
 * @returns {Promise<string>} Plain translated text
 */
export const translateViaChartmindTranslateQuery = async (userInput, options = {}) => {
  const { module = 'chartmind', featureName = 'translation' } = options;
  const response = await llmProviderService.callLLMWithQuery('chartmind-translate', userInput, {
    module,
    featureName,
  });
  const raw =
    typeof response === 'string'
      ? response
      : (response?.output ?? response?.content ?? response?.text ?? '');
  return normalizeTranslationLLMOutput(raw);
};
